import { AGENT_IDS, type AgentId } from '@quintessence/core';
import { BRAND } from '../theme';

/**
 * A quiet field of neurons and synapses drawn on a canvas. Signals wander the
 * graph on their own; in the hero variant four element hubs sit around a core
 * (the logo's quincunx) and an agent's action fires a cascade from its hub
 * through the core and out across the field.
 */

export type FieldVariant = 'hero' | 'ambient';

export interface FieldOptions {
  /** Colour of idle signals and neurons (default: aether). */
  tint?: string;
  /** Where the field is most visible, as fractions of width and height (ambient only). */
  focus?: [number, number];
  /** Scales the visible radius around the focus; large values make the field even. */
  spread?: number;
  /** Area per neuron in px²; smaller is denser. */
  density?: number;
}

interface Neuron {
  ax: number;
  ay: number;
  x: number;
  y: number;
  r: number;
  amp: number;
  phase: number;
  /** 0..1 visibility from the focal mask, so the field fades behind copy. */
  vis: number;
  glow: number;
  tint: string;
  hub: AgentId | 'core' | null;
  links: number[];
}

interface Pulse {
  from: number;
  to: number;
  t: number;
  speed: number;
  color: string;
  hops: number;
  strength: number;
  branch: number;
}

export const AETHER = '#b9acff';
const NODE_RGB = '200, 206, 255';
const MAX_PULSES = 70;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const smooth = (e0: number, e1: number, x: number) => {
  const t = clamp((x - e0) / (e1 - e0), 0, 1);
  return t * t * (3 - 2 * t);
};

function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Pre-rendered radial glow per colour; drawImage is far cheaper than shadowBlur. */
const sprites = new Map<string, HTMLCanvasElement>();
function glowSprite(color: string): HTMLCanvasElement {
  let s = sprites.get(color);
  if (s) return s;
  s = document.createElement('canvas');
  s.width = s.height = 64;
  const g = s.getContext('2d')!;
  const grad = g.createRadialGradient(32, 32, 0, 32, 32, 32);
  grad.addColorStop(0, color);
  grad.addColorStop(0.18, `${color}cc`);
  grad.addColorStop(0.45, `${color}33`);
  grad.addColorStop(1, `${color}00`);
  g.fillStyle = grad;
  g.fillRect(0, 0, 64, 64);
  sprites.set(color, s);
  return s;
}

export class NeuralFieldEngine {
  private readonly ctx: CanvasRenderingContext2D;
  private neurons: Neuron[] = [];
  private edges: [number, number][] = [];
  private pulses: Pulse[] = [];
  private hubs: Partial<Record<AgentId | 'core', number>> = {};
  private w = 0;
  private h = 0;
  private dpr = 1;
  private raf = 0;
  private last = 0;
  private spawnIn = 400;
  private running = false;
  private queue: AgentId[] = [];
  private queueIn = 0;
  private pointer: { x: number; y: number } | null = null;
  private rect: DOMRect | null = null;
  private readonly rng = mulberry32(0x5eed);
  private readonly tint: string;
  readonly still: boolean;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly variant: FieldVariant,
    private readonly opts: FieldOptions = {},
  ) {
    this.ctx = canvas.getContext('2d')!;
    this.tint = opts.tint ?? AETHER;
    this.still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  resize(w: number, h: number) {
    if (w < 2 || h < 2) return;
    this.w = w;
    this.h = h;
    this.dpr = Math.min(2, window.devicePixelRatio || 1);
    this.canvas.width = Math.round(w * this.dpr);
    this.canvas.height = Math.round(h * this.dpr);
    this.build();
    this.pulses = [];
    if (this.still || !this.running) this.draw(performance.now());
  }

  setPointer(clientX: number, clientY: number) {
    this.pointer = { x: clientX, y: clientY };
  }

  clearPointer() {
    this.pointer = null;
  }

  /** Queue an agent's action; cascades are staggered so a burst of actions stays legible. */
  fire(agent: AgentId) {
    if (this.still) return;
    if (this.queue.length < 6) this.queue.push(agent);
  }

  start() {
    if (this.running || this.still) return;
    this.running = true;
    this.last = performance.now();
    const loop = (now: number) => {
      if (!this.running) return;
      this.tick(now);
      this.draw(now);
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
  }

  stop() {
    this.running = false;
    cancelAnimationFrame(this.raf);
  }

  // ---------------------------------------------------------------------------

  private focus() {
    const { w, h } = this;
    const k = this.opts.spread ?? 1;
    if (this.variant === 'ambient') {
      const [x, y] = this.opts.focus ?? [0.5, 0.5];
      return { fx: w * x, fy: h * y, rx: w * 0.55 * k, ry: h * 0.8 * k };
    }
    if (w >= 900) return { fx: w * 0.76, fy: h * 0.3, rx: w * 0.52 * k, ry: h * 0.72 * k };
    return { fx: w * 0.72, fy: h * 0.16, rx: w * 0.85 * k, ry: h * 0.4 * k };
  }

  /** The hero's quincunx sits in the open corner above the headline, like the logo's mark. */
  private constellation() {
    const { w, h } = this;
    if (w >= 900) return { x: w * 0.8, y: clamp(h * 0.12, 84, 150), r: clamp(w * 0.05, 52, 78) };
    return { x: w * 0.8, y: 44, r: 24 };
  }

  private build() {
    const { w, h, rng } = this;
    const hero = this.variant === 'hero';
    const area = w * h;
    const target = clamp(Math.round(area / (this.opts.density ?? (hero ? 10500 : 7000))), 24, 170);
    const cell = Math.sqrt(area / target);
    const { fx, fy, rx, ry } = this.focus();
    const neurons: Neuron[] = [];

    const visAt = (x: number, y: number) => {
      const d = Math.hypot((x - fx) / rx, (y - fy) / ry);
      return 0.1 + 0.9 * smooth(1.05, 0.2, d);
    };
    const add = (x: number, y: number, hub: Neuron['hub'] = null): number => {
      neurons.push({
        ax: x,
        ay: y,
        x,
        y,
        r: hub === 'core' ? 3.4 : hub ? 2.7 : 0.9 + rng() * 0.9,
        amp: hub ? 1.5 : 2 + rng() * 4,
        phase: rng() * Math.PI * 2,
        vis: hub ? 1 : visAt(x, y),
        glow: 0,
        tint: hub === 'core' ? AETHER : hub ? BRAND[hub] : this.tint,
        hub,
        links: [],
      });
      return neurons.length - 1;
    };

    const hubPos: { id: AgentId | 'core'; x: number; y: number }[] = [];
    if (hero) {
      const { x: cx, y: cy, r: R } = this.constellation();
      hubPos.push({ id: 'core', x: cx, y: cy });
      // Same corners as the logo: fire and earth on top, air and water below.
      const corners: [AgentId, number, number][] = [
        ['pyre', -1, -1],
        ['hoard', 1, -1],
        ['rain', -1, 1],
        ['tide', 1, 1],
      ];
      for (const [id, sx, sy] of corners) hubPos.push({ id, x: cx + sx * R, y: cy + sy * R });
    }

    const cols = Math.ceil(w / cell);
    const rows = Math.ceil(h / cell);
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (rng() > 0.86) continue;
        const x = (c + 0.12 + rng() * 0.76) * cell;
        const y = (r + 0.12 + rng() * 0.76) * cell;
        if (hubPos.some((p) => Math.hypot(p.x - x, p.y - y) < Math.min(cell * 0.55, 30))) continue;
        add(x, y);
      }
    }
    this.hubs = {};
    for (const p of hubPos) this.hubs[p.id] = add(p.x, p.y, p.id);

    // k-nearest synapses, deduplicated; hubs reach further and the core wires to every hub.
    const seen = new Set<number>();
    const edges: [number, number][] = [];
    const link = (a: number, b: number) => {
      const key = a < b ? a * 4096 + b : b * 4096 + a;
      if (a === b || seen.has(key)) return;
      seen.add(key);
      edges.push([a, b]);
      neurons[a].links.push(b);
      neurons[b].links.push(a);
    };
    for (let i = 0; i < neurons.length; i++) {
      const n = neurons[i];
      const k = n.hub ? 5 : 3;
      const reach = cell * (n.hub ? 2.3 : 1.75);
      const near = neurons
        .map((m, j) => ({ j, d: Math.hypot(m.ax - n.ax, m.ay - n.ay) }))
        .filter((e) => e.j !== i && e.d < reach)
        .sort((a, b) => a.d - b.d)
        .slice(0, k);
      for (const e of near) link(i, e.j);
    }
    const core = this.hubs.core;
    if (core !== undefined) for (const id of AGENT_IDS) link(core, this.hubs[id]!);

    this.neurons = neurons;
    this.edges = edges;
  }

  private spawn(from: number, to: number, color: string, hops: number, strength: number, speed: number, branch: number) {
    if (this.pulses.length >= MAX_PULSES) return;
    this.pulses.push({ from, to, t: 0, speed, color, hops, strength, branch });
  }

  private spontaneous() {
    const n = this.neurons.length;
    if (!n) return;
    // Bias toward the visible part of the field so most signals are seen.
    let i = Math.floor(this.rng() * n);
    for (let tries = 0; tries < 4 && this.neurons[i].vis < 0.45; tries++) i = Math.floor(this.rng() * n);
    const src = this.neurons[i];
    if (!src.links.length) return;
    const to = src.links[Math.floor(this.rng() * src.links.length)];
    const hub = src.hub && src.hub !== 'core' ? src.hub : null;
    this.spawn(i, to, hub ? BRAND[hub] : this.tint, 2 + Math.floor(this.rng() * 3), 0.55, 0.09 + this.rng() * 0.05, 0.18);
  }

  private cascade(agent: AgentId) {
    const hub = this.hubs[agent];
    const core = this.hubs.core;
    const color = BRAND[agent];
    if (hub === undefined || core === undefined) {
      // No hubs in this variant: ignite a visible neuron instead.
      const pool = this.neurons.flatMap((n, i) => (n.vis > 0.55 && n.links.length ? [i] : []));
      if (!pool.length) return;
      const src = pool[Math.floor(this.rng() * pool.length)];
      this.neurons[src].glow = 1.4;
      this.neurons[src].tint = color;
      for (const to of this.neurons[src].links.slice(0, 3)) this.spawn(src, to, color, 4, 0.95, 0.17, 0.4);
      return;
    }
    this.neurons[hub].glow = 1.6;
    this.spawn(hub, core, color, 6, 1, 0.22, 0.5);
    const local = this.neurons[hub].links.filter((l) => l !== core);
    for (let k = 0; k < Math.min(2, local.length); k++) {
      this.spawn(hub, local[Math.floor(this.rng() * local.length)], color, 3, 0.85, 0.16, 0.35);
    }
  }

  private tick(now: number) {
    const dt = Math.min(48, now - this.last);
    this.last = now;
    const t = now * 0.001;

    for (const n of this.neurons) {
      n.x = n.ax + Math.sin(t * 0.21 + n.phase) * n.amp;
      n.y = n.ay + Math.cos(t * 0.17 + n.phase * 1.3) * n.amp;
      n.glow *= Math.exp(-dt / 700);
    }

    this.spawnIn -= dt;
    if (this.spawnIn <= 0) {
      this.spawnIn = (this.variant === 'hero' ? 320 : 520) + this.rng() * 820;
      this.spontaneous();
    }

    this.queueIn -= dt;
    if (this.queue.length && this.queueIn <= 0) {
      this.cascade(this.queue.shift()!);
      this.queueIn = 420;
    }

    const next: Pulse[] = [];
    for (const p of this.pulses) {
      const a = this.neurons[p.from];
      const b = this.neurons[p.to];
      const len = Math.max(20, Math.hypot(b.x - a.x, b.y - a.y));
      p.t += (dt * p.speed) / len;
      if (p.t < 1) {
        next.push(p);
        continue;
      }
      b.glow = Math.max(b.glow, p.strength);
      if (!b.hub) b.tint = p.color;
      if (p.hops <= 0) continue;
      const options = b.links.filter((l) => l !== p.from);
      if (!options.length) continue;
      const count = this.rng() < p.branch ? 2 : 1;
      for (let k = 0; k < count; k++) {
        const to = options[Math.floor(this.rng() * options.length)];
        if (next.length < MAX_PULSES) next.push({ ...p, from: p.to, to, t: 0, hops: p.hops - 1, strength: p.strength * 0.86 });
      }
    }
    this.pulses = next;
  }

  private draw(now: number) {
    const { ctx, dpr, neurons, edges } = this;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, this.w, this.h);

    // Pointer proximity brightens nearby neurons and their synapses.
    this.rect = this.canvas.getBoundingClientRect();
    const px = this.pointer ? this.pointer.x - this.rect.left : -9999;
    const py = this.pointer ? this.pointer.y - this.rect.top : -9999;
    const boost = (n: Neuron) => {
      const d = Math.hypot(n.x - px, n.y - py);
      return d < 150 ? (1 - d / 150) ** 2 : 0;
    };

    // Synapses in three alpha buckets: three strokes instead of one per edge.
    ctx.lineWidth = 1;
    const buckets: [number, number][][] = [[], [], []];
    for (const e of edges) {
      const v = Math.min(neurons[e[0]].vis, neurons[e[1]].vis);
      buckets[v > 0.66 ? 2 : v > 0.33 ? 1 : 0].push(e);
    }
    const alphas = [0.03, 0.06, 0.1];
    buckets.forEach((list, i) => {
      if (!list.length) return;
      ctx.strokeStyle = `rgba(${NODE_RGB}, ${alphas[i]})`;
      ctx.beginPath();
      for (const [a, b] of list) {
        ctx.moveTo(neurons[a].x, neurons[a].y);
        ctx.lineTo(neurons[b].x, neurons[b].y);
      }
      ctx.stroke();
    });

    // The constellation's own wiring reads a little brighter, like the logo's mark.
    const core = this.hubs.core;
    if (core !== undefined) {
      ctx.lineWidth = 1;
      for (const id of AGENT_IDS) {
        const hub = neurons[this.hubs[id]!];
        const grad = ctx.createLinearGradient(hub.x, hub.y, neurons[core].x, neurons[core].y);
        grad.addColorStop(0, `${BRAND[id]}55`);
        grad.addColorStop(1, `${AETHER}33`);
        ctx.strokeStyle = grad;
        ctx.beginPath();
        ctx.moveTo(hub.x, hub.y);
        ctx.lineTo(neurons[core].x, neurons[core].y);
        ctx.stroke();
      }
    }

    const lit = this.pointer ? neurons.map(boost) : null;
    if (lit) {
      ctx.beginPath();
      ctx.strokeStyle = `rgba(${NODE_RGB}, 0.16)`;
      neurons.forEach((n, i) => {
        if (lit[i] < 0.05) return;
        for (const l of n.links) {
          ctx.moveTo(n.x, n.y);
          ctx.lineTo(neurons[l].x, neurons[l].y);
        }
      });
      ctx.stroke();
    }

    // Signals: a fading trail along the synapse and a glowing head.
    for (const p of this.pulses) {
      const a = neurons[p.from];
      const b = neurons[p.to];
      const head = Math.min(1, p.t);
      const tail = Math.max(0, head - 0.4);
      const hx = a.x + (b.x - a.x) * head;
      const hy = a.y + (b.y - a.y) * head;
      const tx = a.x + (b.x - a.x) * tail;
      const ty = a.y + (b.y - a.y) * tail;
      const vis = Math.max(0.4, a.vis + (b.vis - a.vis) * head);
      const grad = ctx.createLinearGradient(tx, ty, hx, hy);
      grad.addColorStop(0, `${p.color}00`);
      grad.addColorStop(1, p.color);
      ctx.globalAlpha = 0.75 * p.strength * vis;
      ctx.strokeStyle = grad;
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.moveTo(tx, ty);
      ctx.lineTo(hx, hy);
      ctx.stroke();
      const s = 10 + 8 * p.strength;
      ctx.globalAlpha = 0.9 * p.strength * vis;
      ctx.drawImage(glowSprite(p.color), hx - s / 2, hy - s / 2, s, s);
    }
    ctx.globalAlpha = 1;

    // Neurons; hubs breathe slowly and flare when their agent acts.
    const t = now * 0.001;
    neurons.forEach((n, i) => {
      const b = lit ? lit[i] : 0;
      if (n.hub) {
        const breathe = 0.5 + 0.5 * Math.sin(t * 0.9 + n.phase);
        const halo = (n.hub === 'core' ? 46 : 30) * (1 + 0.5 * Math.min(1, n.glow));
        ctx.globalAlpha = clamp(0.22 + 0.12 * breathe + 0.5 * n.glow, 0, 1);
        ctx.drawImage(glowSprite(n.tint), n.x - halo / 2, n.y - halo / 2, halo, halo);
        ctx.globalAlpha = 1;
        ctx.fillStyle = n.hub === 'core' ? '#f4f2ff' : n.tint;
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fill();
        return;
      }
      if (n.glow > 0.04) {
        const s = 14 + 14 * Math.min(1, n.glow);
        ctx.globalAlpha = clamp(n.glow * 0.8, 0, 1) * Math.max(0.5, n.vis);
        ctx.drawImage(glowSprite(n.tint), n.x - s / 2, n.y - s / 2, s, s);
      }
      ctx.globalAlpha = clamp((0.26 + 0.6 * b) * n.vis + n.glow * 0.6, 0, 1);
      ctx.fillStyle = `rgb(${NODE_RGB})`;
      ctx.beginPath();
      ctx.arc(n.x, n.y, n.r + b * 0.8, 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.globalAlpha = 1;
  }
}
